import type { UUID } from '@cobliteam/common-types';

import { GraphQLUserAlertSettingsRequester } from '@cobliteam/api-lib';
import { getDefaultSetupSettings } from '@cobliteam/api-lib/src/dashboard/userAlert/settings/definitions';

import { Environment } from '@cobliteam/react-environments';

const { oneSignalAppId } = Environment.getInstance().getEnvironment();

declare const OneSignalDeferred: any;
declare const OneSignal: any; // OneSignal global variable initialized on index.html

let hasBeenInitialized = false;

export class Notifications {
  public static isInitialized = () => hasBeenInitialized;

  public static initialize = (fleetId: UUID, fleetName: string, userId: UUID) => {
    if (hasBeenInitialized) {
      console.warn('Notifications has already been initialized');
      Notifications.setUser(fleetId, fleetName, userId);
      return;
    }

    OneSignalDeferred.push(async (OneSignal: any) => {
      await OneSignal.init({
        appId: oneSignalAppId,
        autoRegister: true,
        // ⚠️ WARNING: Service Worker Configuration for OneSignal
        // Ensure the correct path and scope are set for the service worker.
        // In this instance, the service worker is located in the public/push/original directory of the project.
        // Modifying this path could conflict with other service workers (like PWA's service worker) and potentially break the entire application.
        // Proceed with caution make sure you understand the implications of changing the service worker path and scope.
        serviceWorkerParam: { scope: '/push/onesignal/' },
        serviceWorkerPath: '/push/onesignal/OneSignalSDKWorker.js',
      });

      hasBeenInitialized = true;

      // show the native prompt
      await OneSignal.Slidedown.promptPush();

      Notifications.setUser(fleetId, fleetName, userId);

      // when the user changes the permission, we need to update the settings
      await OneSignal.Notifications.addEventListener('permissionChange', () => {
        const userAlertSettingsRequester = new GraphQLUserAlertSettingsRequester();
        userAlertSettingsRequester.setupSettings(getDefaultSetupSettings());
      });
    });
  };

  public static setUser = (fleetId: UUID, fleetName: string, userId: UUID) => {
    OneSignalDeferred.push(async (OneSignal: any) => {
      // create a new user
      await OneSignal.login(userId);

      // add tags
      await OneSignal.User.addTags({
        fleet_id: fleetId,
        fleet_name: fleetName,
      });

      console.info('OneSignal user set!');
    });
  };

  public static hasNotificationPermission = (): boolean => {
    return OneSignal?.Notifications?.permission ?? false;
  };

  public static slidedownPromptPush(options?: any): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        OneSignalDeferred?.push((OneSignal: any) => {
          OneSignal.Slidedown.promptPush(options)
            .then((value: any) => resolve(value))
            .catch((error: any) => reject(error));
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  public static pushSubscriptionOptIn(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        OneSignalDeferred?.push((OneSignal: any) => {
          OneSignal.User.PushSubscription.optIn()
            .then((value: any) => resolve(value))
            .catch((error: any) => reject(error));
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  public static pushSubscriptionOptOut(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        OneSignalDeferred?.push((OneSignal: any) => {
          OneSignal.User.PushSubscription.optOut()
            .then((value: any) => resolve(value))
            .catch((error: any) => reject(error));
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  public static getUserInfo() {
    Promise.all([
      OneSignal.Notifications.permission,
      OneSignal.User.PushSubscription.id,
      OneSignal.User.PushSubscription.token,
      OneSignal.User.PushSubscription.optedIn,
      OneSignal.context.serviceWorkerManager.getActiveState(),
    ])
      .then(([isSubscribed, subscriptionId, pushToken, optedIn, serviceWorkerActive]) => {
        console.info('What is the current URL of this page?', location.href);
        console.info("Is a service worker registered and active? (should be false on Safari, otherwise should be 'OneSignal Worker')?", serviceWorkerActive);
        console.info('Are you subscribed, in the browser?', isSubscribed);
        console.info('Are you opted-in, in OneSignal?', optedIn);
        console.info('What is your OneSignal Subscription ID?', subscriptionId);
        console.info('What is your Push Token?', pushToken);
      })
      .catch((e) => {
        console.error('Issue determining whether push is enabled:', e);
      });
  }

  public static updateSubscription = async (value: Boolean) => {
    if (!hasBeenInitialized) await Notifications.slidedownPromptPush({ force: true });
    if (value) {
      const hasPemission = Notifications.hasNotificationPermission();
      if (!hasPemission) OneSignal.Notifications.requestPermission();
      await Notifications.pushSubscriptionOptIn();
    } else {
      await Notifications.pushSubscriptionOptOut();
    }
  };
}
